import type { EnumUnion } from '@dresscode-frontend/utility-types';

import type {
  GetAttendanceSummaryDailyPlaceListItem,
  GetAttendanceSummaryMonthlyPersonListItem,
  GetAttendanceSummaryMonthlyPlaceListItem,
} from '../../../../-attendance-api-types';

// 共通の型定義
type AttendanceTablePersonColumn = {
  id: string;
  avatarUrl?: string;
  name: string;
  email?: string;
  linkTo: string;
};

type AttendanceTableClockInColumn =
  GetAttendanceSummaryDailyPlaceListItem['clockIns'][number];
type AttendanceTableClockOutColumn =
  GetAttendanceSummaryDailyPlaceListItem['clockOuts'][number];
type AttendanceTableRestTimeColumn = {
  totalRestTimeMinutes: number;
  restTimes: GetAttendanceSummaryDailyPlaceListItem['restTimes'][number][];
  isEdited: boolean;
  editReason?: string;
};

// 共通のカラム定義
export const AttendanceTableColumns = {
  PERSON: 'person',
  DATE: 'date',
  CLOCK_INS: 'clockIns',
  CLOCK_OUTS: 'clockOuts',
  TOTAL_WORKING_TIME_MINUTES: 'totalWorkingTimeMinutes',
  REST_TIMES: 'restTimes',
  IS_VALID: 'isValid',
  ERRORS: 'errors',
} as const;

export type AttendanceTableColumns = EnumUnion<typeof AttendanceTableColumns>;

// 共通の行の型定義
export type AttendanceTableRow = {
  [AttendanceTableColumns.PERSON]?: AttendanceTablePersonColumn;
  [AttendanceTableColumns.DATE]: Date;
  [AttendanceTableColumns.CLOCK_INS]: AttendanceTableClockInColumn[];
  [AttendanceTableColumns.CLOCK_OUTS]: AttendanceTableClockOutColumn[];
  [AttendanceTableColumns.REST_TIMES]: AttendanceTableRestTimeColumn;
  [AttendanceTableColumns.TOTAL_WORKING_TIME_MINUTES]: number;
  [AttendanceTableColumns.IS_VALID]: boolean;
  [AttendanceTableColumns.ERRORS]: string[];
};

// 日次・場所別テーブル用の型
export type AttendanceDailyPlaceTableRow = AttendanceTableRow;

// 月次・人物別テーブル用の型
export type AttendanceMonthlyPersonTableRow = AttendanceTableRow & {
  [AttendanceTableColumns.DATE]: Date;
};

// 変換関数
export const convertAttendanceDailyPlaceItemToTableRow = (
  item: GetAttendanceSummaryDailyPlaceListItem,
): AttendanceDailyPlaceTableRow => {
  // 代表して最後のレコードの理由を使用
  const restTimeEditReason = item.restTimes
    .filter((e) => !!e.editReason)
    .at(-1)?.editReason;
  return {
    [AttendanceTableColumns.PERSON]: {
      id: item.person.personId,
      name: item.person.personName,
      avatarUrl: item.person.avatarUrl,
      linkTo: `/time-and-attendance/summary/monthly-person?personId=${item.person.personId}`,
    },
    [AttendanceTableColumns.DATE]: new Date(
      item.date.year,
      item.date.month - 1,
      item.date.day,
    ),
    [AttendanceTableColumns.CLOCK_INS]: item.clockIns,
    [AttendanceTableColumns.CLOCK_OUTS]: item.clockOuts,
    [AttendanceTableColumns.REST_TIMES]: {
      totalRestTimeMinutes: item.totalBreakTimeMinutes,
      restTimes: item.restTimes,
      isEdited: item.restTimes.some((e) => e.isEdited),
      editReason: restTimeEditReason,
    },
    [AttendanceTableColumns.TOTAL_WORKING_TIME_MINUTES]:
      item.totalWorkingTimeMinutes,
    [AttendanceTableColumns.IS_VALID]: item.isValid,
    [AttendanceTableColumns.ERRORS]: [
      ...item.clockInErrors,
      ...item.clockOutErrors,
      ...item.restTimes.flatMap((restTime) => restTime.errors),
    ],
  };
};

export const convertAttendanceMonthlyPersonItemToTableRow = (
  item: GetAttendanceSummaryMonthlyPersonListItem,
): AttendanceMonthlyPersonTableRow => {
  // 代表して最後のレコードの理由を使用
  const restTimeEditReason = item.restTimes
    .filter((e) => e.editReason)
    .at(-1)?.editReason;
  return {
    [AttendanceTableColumns.DATE]: new Date(
      item.date.year,
      item.date.month - 1,
      item.date.day,
    ),
    [AttendanceTableColumns.CLOCK_INS]: item.clockIns,
    [AttendanceTableColumns.CLOCK_OUTS]: item.clockOuts,
    [AttendanceTableColumns.REST_TIMES]: {
      totalRestTimeMinutes: item.totalBreakTimeMinutes,
      restTimes: item.restTimes,
      isEdited: item.restTimes.some((e) => e.isEdited),
      editReason: restTimeEditReason,
    },
    [AttendanceTableColumns.TOTAL_WORKING_TIME_MINUTES]:
      item.totalWorkingTimeMinutes,
    [AttendanceTableColumns.IS_VALID]: item.isValid,
    [AttendanceTableColumns.ERRORS]: [
      ...item.clockInErrors,
      ...item.clockOutErrors,
      ...item.restTimes.flatMap((restTime) => restTime.errors),
    ],
  };
};

export const MonthlyPlaceTableColumns = {
  PERSON: 'person',
  WORKING_DAYS: 'workingDays',
  TOTAL_WORKING_TIME: 'totalWorkingTime',
  ERROR_COUNT: 'errorCount',
} as const;

export type MonthlyPlaceTableColumns = EnumUnion<
  typeof MonthlyPlaceTableColumns
>;

export type MonthlyPlaceTableRow = {
  [MonthlyPlaceTableColumns.PERSON]: {
    id: string;
    name: string;
    avatarUrl?: string;
    linkTo: string;
  };
  [MonthlyPlaceTableColumns.WORKING_DAYS]: number;
  [MonthlyPlaceTableColumns.TOTAL_WORKING_TIME]: number;
  [MonthlyPlaceTableColumns.ERROR_COUNT]: number;
};

export const convertAttendanceMonthlyPlaceItemToTableRow = (
  item: GetAttendanceSummaryMonthlyPlaceListItem,
): MonthlyPlaceTableRow => {
  return {
    [MonthlyPlaceTableColumns.PERSON]: {
      id: item.person.personId,
      name: item.person.personName,
      avatarUrl: item.person.avatarUrl,
      linkTo: `/time-and-attendance/summary/monthly-person?personId=${item.person.personId}`,
    },
    [MonthlyPlaceTableColumns.WORKING_DAYS]: item.totalWorkingDays,
    [MonthlyPlaceTableColumns.TOTAL_WORKING_TIME]: item.totalWorkingTimeMinutes,
    [MonthlyPlaceTableColumns.ERROR_COUNT]: item.errorDayCount,
  };
};
